import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect, type MutableRefObject } from "react";
import type Lenis from "lenis";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import LandingPage from "./pages/Landing";
import HowItWorksPage from "./pages/HowItWorks";
import PlatformsPage from "./pages/Platforms";
import TradableAssetsPage from "./pages/TradableAssets";
import SupportPage from "./pages/Support";
import SearchPage from "./pages/Search";
import SupportWidget from "./components/common/SupportWidget";
import { useSmoothScroll } from "./components/common/useSmoothScroll";
import { useSiteNavigationStructuredData } from "./lib/seo";
import { LocaleProvider } from "./i18n/LocaleProvider";
import { LOCALES } from "./i18n/locales";
import { localeFromPath, ROUTE_IDS, pathFor, type RouteId } from "./routes/manifest";
import NotFoundPage from "./pages/NotFound";

function ScrollToTop({ lenisRef }: { lenisRef: MutableRefObject<Lenis | null> }): null {
  const { pathname } = useLocation();
  useEffect(() => {
    // When Lenis is active, route the jump through it so its
    // internal scroll position state stays in sync. Fall back to
    // the native API for the first paint (before Lenis has mounted)
    // and for users with `prefers-reduced-motion` who skipped Lenis
    // init entirely.
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, lenisRef]);
  return null;
}

const PAGE_FOR: Record<RouteId, JSX.Element> = {
  "/": <LandingPage />,
  "/how-it-works": <HowItWorksPage />,
  "/platforms": <PlatformsPage />,
  "/tradable-assets": <TradableAssetsPage />,
  "/support": <SupportPage />,
  "/search": <SearchPage />,
};

function ConditionalSupportWidget(): JSX.Element | null {
  const { pathname } = useLocation();
  // Compare the resolved ROUTE, not the raw path, so the widget is hidden on
  // /de/support exactly as it is on /support.
  if (localeFromPath(pathname) && pathname.endsWith("/support")) return null;
  return <SupportWidget />;
}

export default function App(): JSX.Element {
  const lenisRef = useSmoothScroll();
  // One resolved locale feeds both the structured data and the provider, so the
  // JSON-LD a crawler reads and the copy a reader sees can never disagree.
  // Derived from the URL through the manifest; every consumer already read it
  // from context, so this was a one-line change rather than a sweep.
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname);
  useSiteNavigationStructuredData(locale);

  return (
    <LocaleProvider locale={locale}>
      <ScrollToTop lenisRef={lenisRef} />
      <Header />
      <main>
        {/*
          * Generated from the manifest rather than hand-listed, so a route added
          * there becomes routable in BOTH locales with no edit here — the
          * property that keeps the router, the switcher, the hreflang set and
          * the sitemap from drifting apart.
          */}
        <Routes>
          {LOCALES.flatMap((loc) =>
            ROUTE_IDS.map((routeId) => (
              <Route
                key={`${loc}${routeId}`}
                path={pathFor(routeId, loc)}
                element={PAGE_FOR[routeId]}
              />
            )),
          )}
          {/* Narrowed from the old catch-all, which rendered Landing — and so
              returned a 200 homepage for /nonsense and every typo'd locale. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <ConditionalSupportWidget />
    </LocaleProvider>
  );
}
